import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, MessageSquare, HelpCircle } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";

// Phase A: Contact Us / Complaint / Inquiry screen for Retailer + Wholesaler.
// Tapping "Call Company" starts a tel: dial to the configured company phone.
export default function ContactUs() {
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [phone, setPhone] = useState("");

  useEffect(() => {
    api.get("/settings/public")
      .then(r => setPhone(r.data?.company_phone || ""))
      .catch(() => setPhone(""));
  }, []);

  const display = phone || "+91 0000 000 000";
  const dialHref = phone ? `tel:${phone.replace(/\s+/g, "")}` : "tel:+910000000000";

  const bullets = lang === "ta"
    ? [
        "ஆர்டர் தொடர்பா பிரச்சினை",
        "தயாரிப்பு தரம் / அளவு புகார்",
        "டெலிவரி டெலே / தவறான கடை",
        "விலை / விற்பனை விசாரணை",
      ]
    : [
        "Order-related issue",
        "Product quality / quantity complaint",
        "Delivery delay / wrong shop",
        "Pricing / sales inquiry",
      ];

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10">
      <TopBar title={t("complaint_inquiry")} onBack={() => nav("/shop")} />
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-5 fade-up">
        {/* Hero Contact Card */}
        <div className="bg-gradient-to-br from-[#0A1A3F] to-[#0055FF] text-white rounded-3xl p-6 sm:p-8 shadow-xl" data-testid="contact-hero">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-white/15 grid place-items-center">
              <HelpCircle className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/70 font-bold">{t("contact_us")}</div>
              <div className="heading-font text-2xl font-bold">{t("company_support")}</div>
            </div>
          </div>
          <div className="text-sm text-white/80 mb-6">
            {lang === "ta"
              ? "உங்கள் கேள்வி, புகார் அல்லது ஆர்டர் உதவிக்கு எங்கள் கம்பெனி எண்ணை தொடர்பு கொள்ளுங்கள்."
              : "For any question, complaint, or order help — tap below to call our team directly."}
          </div>
          <a
            data-testid="contact-call-btn"
            href={dialHref}
            className="w-full bg-white text-[#0A1A3F] hover:bg-white/95 rounded-2xl py-4 font-bold text-lg flex items-center justify-center gap-3 shadow-lg active:scale-[0.98] transition"
          >
            <Phone className="w-5 h-5" />
            <span data-testid="contact-phone-number">{display}</span>
          </a>
        </div>

        {/* What can you call about */}
        <div className="bg-white rounded-2xl p-5 soft-shadow" data-testid="contact-topics-card">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-5 h-5 text-[#0055FF]" />
            <div className="heading-font font-bold text-lg text-[#1D1D1F]">
              {lang === "ta" ? "எதற்காக கூப்பிடலாம்?" : "What can you call about?"}
            </div>
          </div>
          <ul className="space-y-2">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#1D1D1F]">
                <span className="text-[#0055FF] mt-0.5">•</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
